import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useDebouncedCallback } from '@bettermode/common/hooks'
import {
  Chat,
  ChatStatus,
  ChatType,
  Message,
  MessageList,
  QueryDirection,
} from '@tribeplatform/gql-client/types'
import {
  useAdvanceReadMessageOffset,
  useUnreadMessagesCount,
  useChat,
  useChatsReadOffset,
  useGetChatParticipantsByIds,
  useMessages,
  usePendingMessages,
  useRemovePendingMessage,
} from '@tribeplatform/react-sdk/hooks'
import {
  VirtualList,
  VirtualListImperativeRef,
} from '@tribeplatform/react-ui-kit/VirtualList'

import {
  MsgReceivedEvent,
  TMsgReceivedServerPayload,
  useSocket,
} from '../../Providers/index.js'
import { useChatContext } from '../ChatContext/ChatContext.js'

import { ChatMessage } from './ChatMessage/ChatMessage.js'
import { ChatMessagesListHeader } from './ChatMessagesListHeader.js'
import { SelectedMessageOverlay } from './SelectedMessageOverlay.js'
import { isPendingMessage } from './utils/chat.utils.js'

const OVERSCAN_SIZE = 25
const DEFAULT_ESTIMATED_SIZE = 96
const HEADER_HEIGHT = 50
const MESSAGES_PAGE_LIMIT = 30
const HANDLE_VISIBLE_RANGE_DEBOUNCE_TIME = 500

interface Props {
  chatId: Chat['_id']
}

export const ChatMessages = forwardRef<VirtualListImperativeRef, Props>(
  ({ chatId }, ref) => {
    const [isInitialized, setIsInitialized] = useState(false)
    const overscan = useRef(OVERSCAN_SIZE)
    const { data: chat, isFetched: hasFetchedChat } = useChat({
      variables: { id: chatId },
    })
    const [selectedMessage, setSelectedMessage] = useState<Message | null>(null)
    const { setMessageToEdit } = useChatContext()

    const { socket } = useSocket()
    const { lastMessage: lastChatMessage, currentParticipant } = chat
    const hasReadMarker = !!currentParticipant?.lastReadMessage
    const isEmptyChat = !lastChatMessage
    const loadingParticipants = useRef(false)
    const virtualListRef = useRef<VirtualListImperativeRef>(null)

    useImperativeHandle(ref, () => virtualListRef.current)

    const [markerMessageAddress, setMarkerMessageAddress] = useState<{
      chatId: string
      pageId: string
      messageId: string
      addressSignature: string
    }>(null)
    const initialRenderIndex = useRef<number>(null)

    useEffect(() => {
      if (markerMessageAddress || !hasFetchedChat) {
        return
      }

      if (hasReadMarker) {
        const { lastReadMessage } = currentParticipant
        setMarkerMessageAddress({
          chatId,
          pageId: lastReadMessage.pageId,
          messageId: lastReadMessage.messageId,
          addressSignature: lastReadMessage.signature,
        })
      } else if (lastChatMessage) {
        setMarkerMessageAddress({
          chatId,
          pageId: lastChatMessage.pageId,
          messageId: lastChatMessage.messageId,
          addressSignature: lastChatMessage.addressSignature,
        })
      }
    }, [
      chatId,
      currentParticipant,
      hasFetchedChat,
      hasReadMarker,
      lastChatMessage,
      markerMessageAddress,
    ])
    const { data: pendingMessages } = usePendingMessages({
      variables: { id: chatId },
    })
    const { mutate: removePendingMessages } = useRemovePendingMessage()
    const { getChatParticipantsByIds } = useGetChatParticipantsByIds({
      chatId,
    })
    const { chatsReadOffsets } = useChatsReadOffset()
    const { unreadMessagesCount } = useUnreadMessagesCount()

    const onNewMessagesLoaded = useCallback(
      (data: MessageList[]) => {
        const messages = data.flatMap(page => page.data)
        removePendingMessages({
          chatId,
          nonces: messages.map(message => message.nonce),
        })
      },
      [chatId, removePendingMessages],
    )

    const {
      data: olderMessages,
      fetchNextPage: fetchOlderMessages,
      hasNextPage: hasOlderMessages,
      isFetchingNextPage: isFetchingOlderMessages,
      isLoading: isLoadingOlderMessages,
    } = useMessages({
      variables: {
        ...markerMessageAddress,
        inclusive: true,
        limit: MESSAGES_PAGE_LIMIT,
        direction: QueryDirection.Before,
      },
      useInfiniteQueryOptions: {
        enabled: !!markerMessageAddress,
        refetchInterval: false,
      },
    })

    const {
      data: newMessages,
      fetchNextPage: fetchNewerMessages,
      hasNextPage: hasNewerMessages,
      refetch: refetchNewMessages,
      isFetchingNextPage: isFetchingNewMessages,
      isLoading: isLoadingRecentMessages,
    } = useMessages({
      variables: {
        ...markerMessageAddress,
        inclusive: isEmptyChat,
        limit: MESSAGES_PAGE_LIMIT,
        direction: QueryDirection.After,
      },
      useInfiniteQueryOptions: {
        enabled: !!markerMessageAddress,
        refetchInterval: false,
        onSettled: data => onNewMessagesLoaded(data.pages),
      },
    })

    const refreshNewMessages = useCallback(async () => {
      if (isFetchingNewMessages) {
        return
      }

      await refetchNewMessages<MessageList>({
        refetchPage: (lastPage, index, allPages) => {
          // We only need to refetch the last page with endCursor as null. However, if there are no new messages, which
          // means the markerMessageAddress is not set and the After query is not enabled,
          // There would be no page that has endCursor as null. So refetching the last page if no
          // page has endCursor as null.
          const isLastPage = index === allPages.length - 1
          return lastPage.pageInfo.endCursor === null || isLastPage
        },
      })
    }, [refetchNewMessages, isFetchingNewMessages])

    useEffect(() => {
      const handleMessageReceived = (ev: TMsgReceivedServerPayload) => {
        if (ev.chatId === chatId) {
          if (isEmptyChat) {
            setMarkerMessageAddress({
              chatId: ev.chatId,
              pageId: ev.pageId,
              messageId: ev.messageId,
              addressSignature: ev.addressSignature,
            })
          }
          refreshNewMessages()
        }
      }
      socket.on(MsgReceivedEvent, handleMessageReceived)
      return () => {
        socket.off(MsgReceivedEvent, handleMessageReceived)
      }
    }, [refreshNewMessages, socket, chatId, isEmptyChat])

    const handleScrollTop = useCallback(async () => {
      if (hasOlderMessages && !isFetchingOlderMessages) {
        await fetchOlderMessages()
      }
    }, [fetchOlderMessages, hasOlderMessages, isFetchingOlderMessages])

    const handleScrollBottom = useCallback(async () => {
      if (isFetchingNewMessages) {
        return
      }
      if (hasNewerMessages) {
        await fetchNewerMessages()
      } else {
        await refreshNewMessages()
      }
    }, [
      isFetchingNewMessages,
      hasNewerMessages,
      fetchNewerMessages,
      refreshNewMessages,
    ])

    const flattenedOlderMessages = useMemo(() => {
      return [...(olderMessages?.pages ?? [])]
        .reverse()
        .flatMap(({ data }) => data)
    }, [olderMessages?.pages])

    const flattenedNewerMessages = useMemo(() => {
      // Pages has to reversed since most recent message are in last object of the array
      return (newMessages?.pages ?? []).flatMap(({ data }) => data)
    }, [newMessages])

    const flattenedMessages = useMemo(() => {
      return [
        ...flattenedOlderMessages,
        ...flattenedNewerMessages,
        ...pendingMessages,
      ]
    }, [flattenedOlderMessages, flattenedNewerMessages, pendingMessages])

    const sortedMessagesRef = useRef(flattenedMessages)
    sortedMessagesRef.current = flattenedMessages

    const getItemKey = useCallback((index: number) => {
      return sortedMessagesRef.current?.[index]?._id
    }, [])

    useEffect(() => {
      if (initialRenderIndex.current !== null) {
        return
      }

      if (
        !hasFetchedChat ||
        (!isLoadingRecentMessages && !isLoadingOlderMessages)
      ) {
        if (hasReadMarker) {
          const initialIndex = sortedMessagesRef.current.findIndex(
            message =>
              message._id === currentParticipant?.lastReadMessage?.messageId,
          )
          initialRenderIndex.current = initialIndex + 1
        } else {
          initialRenderIndex.current = sortedMessagesRef.current.length
        }
        /**
         * If the initial index fall outside of the overscan limit, the virtualizer wont’t be able to
         * adjust its position properly
         */
        overscan.current = Math.max(
          sortedMessagesRef.current.length - initialRenderIndex.current,
          overscan.current,
        )
        setIsInitialized(true)
      }
    }, [
      isLoadingRecentMessages,
      isLoadingOlderMessages,
      hasFetchedChat,
      hasReadMarker,
      markerMessageAddress,
      currentParticipant?.lastReadMessage?.messageId,
    ])

    const { mutateAsync: mutateAdvanceOffset } = useAdvanceReadMessageOffset()

    const handleReadOffset = useCallback(
      async (range: { startIndex: number; endIndex: number }) => {
        if (!isInitialized) {
          return
        }

        const index = Math.min(
          range.endIndex,
          sortedMessagesRef.current.length - 1,
        )
        const message = sortedMessagesRef.current.at(index)

        if (!message || isPendingMessage(message)) {
          return
        }
        const { pos } = message
        const offset = chatsReadOffsets[chatId]
        const hasUnreadMessages = !!unreadMessagesCount[chatId]
        const isArchived = chat.currentParticipant?.archivedAt !== null

        if ((offset && offset >= pos) || !hasUnreadMessages) {
          return
        }

        try {
          await mutateAdvanceOffset({
            chatId,
            input: {
              pageId: message.pageId,
              messageId: message._id,
              signature: message.addressSignature,
            },
            pos: message.pos,
            archived: isArchived,
          })
        } catch (e) {
          // No-op
        }
      },
      [
        isInitialized,
        chatsReadOffsets,
        chatId,
        unreadMessagesCount,
        chat.currentParticipant?.archivedAt,
        mutateAdvanceOffset,
      ],
    )

    const getVisibleMessagesParticipants = useCallback(
      async (startIndex: number, endIndex: number) => {
        if (loadingParticipants.current || chat?.type === ChatType.Direct) {
          return
        }
        const visibleMessages = sortedMessagesRef.current.slice(
          startIndex,
          endIndex + 1,
        )
        const visibleMessagesAuthorIds = new Set(
          visibleMessages
            .map(message => (message as Message).authorParticipantId)
            .filter(Boolean),
        )
        loadingParticipants.current = true
        await getChatParticipantsByIds(Array.from(visibleMessagesAuthorIds))
        loadingParticipants.current = false
      },
      [chat?.type, getChatParticipantsByIds],
    )

    const handleVisibleRangeChanged = useCallback(
      async ({
        startIndex,
        endIndex,
      }: {
        startIndex: number
        endIndex: number
      }) => {
        handleReadOffset({ startIndex, endIndex })

        if (chat?.status === ChatStatus.Active) {
          const startingIndex = Math.max(startIndex, 0)
          await getVisibleMessagesParticipants(startingIndex, endIndex)
        }
      },
      [getVisibleMessagesParticipants, handleReadOffset, chat?.status],
    )

    const debouncedHandleVisibleRangeChanged = useDebouncedCallback(
      handleVisibleRangeChanged,
      HANDLE_VISIBLE_RANGE_DEBOUNCE_TIME,
    )

    const onEditMessage = useCallback(
      async (message: Message) => {
        const canEdit = await setMessageToEdit(message)
        if (canEdit) {
          virtualListRef.current?.scrollToItem(message._id)
        }
      },
      [setMessageToEdit],
    )

    const renderItem = useCallback(
      (message: Message, index: number, items: Message[]) => (
        <div className="pt-4">
          <ChatMessage
            chat={chat}
            isLestReadMessage={message._id === markerMessageAddress?.messageId}
            isLastMessage={index === items.length - 1}
            id={`message-${message._id}`}
            key={message._id}
            message={message}
            onSelected={() => setSelectedMessage(message)}
            onEdit={onEditMessage}
          />
        </div>
      ),
      [chat, markerMessageAddress?.messageId, onEditMessage],
    )

    const itemComparator = useCallback(
      (messageA: Message, messageB: Message) => {
        return messageA && messageB && messageA._id === messageB._id
      },
      [],
    )
    const showLoader = isFetchingOlderMessages || hasOlderMessages
    const showNoMoreMessagesHeader =
      chat?.type === ChatType.Direct &&
      !hasOlderMessages &&
      !isFetchingOlderMessages
    const showHeaderComponent = showLoader || showNoMoreMessagesHeader

    if (!isInitialized) {
      return null
    }

    return (
      <div className="relative h-full w-full">
        <VirtualList
          key={chatId}
          ref={virtualListRef}
          items={flattenedMessages}
          getItemKey={getItemKey}
          onVisibleRangeChanged={debouncedHandleVisibleRangeChanged}
          onHeadReached={handleScrollTop}
          onTailReached={handleScrollBottom}
          itemRenderer={renderItem}
          itemComparator={itemComparator}
          estimateSize={() => DEFAULT_ESTIMATED_SIZE}
          overscan={overscan.current}
          headerHeight={HEADER_HEIGHT}
          headerInnerComponent={
            showHeaderComponent && (
              <ChatMessagesListHeader
                hasOlderMessages={hasOlderMessages}
                isLoading={isFetchingOlderMessages}
                type={chat?.type}
              />
            )
          }
          initialIndex={initialRenderIndex.current}
          autoTail={!hasNewerMessages}
        />
        {selectedMessage && (
          <SelectedMessageOverlay
            selectedMessage={selectedMessage}
            onEditMessage={onEditMessage}
            onClose={() => setSelectedMessage(null)}
          />
        )}
      </div>
    )
  },
)
